import { Dialog, DialogContent, DialogTitle, DialogTrigger } from 'shared/components/ui/dialog';
import { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import { useFileOperations } from '@dev-streetbeat/chat';

import type { File } from '@dev-streetbeat/chat';

import styles from './SvgFileArtifact.module.scss';

type Props = {
  file: File;
  onFinishLoading?: () => void;
};
export const SvgFileArtifact = forwardRef<{ expandSvg: () => void }, Props>(({ file, onFinishLoading }, ref) => {
  useImperativeHandle(ref, () => ({
    expandSvg: () => {
      openDialog();
    },
    downloadSvg: () => {
      download();
    },
  }));

  const dialogTriggerRef = useRef(null);

  const openDialog = () => {
    dialogTriggerRef.current?.click();
  };

  const [content, setContent] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);

  const { downloadFile } = useFileOperations();

  useEffect(() => {
    downloadFile(file.id).then(async (response) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const svgContent = reader.result as string;
        setContent(svgContent);
        onFinishLoading?.();
      };
      reader.readAsText(response);
    });
  }, [file.id, onFinishLoading]);

  useEffect(() => {
    if (content && containerRef.current) {
      const svgElement = containerRef.current.querySelector('svg');
      if (svgElement) {
        const viewBox = svgElement.getAttribute('viewBox');
        if (viewBox) {
          const [, , width, height] = viewBox.split(' ').map(Number);
          const aspectRatio = width / height;
          containerRef.current.style.paddingBottom = `${(1 / aspectRatio) * 100}%`;
        }
        svgElement.setAttribute('width', '100%');
        svgElement.setAttribute('height', '100%');
        svgElement.setAttribute('preserveAspectRatio', 'xMinYMin meet');
      }
    }
  }, [content]);

  const download = async () => {
    const fileName = file.name;
    const fileId = file.id;

    const response = await downloadFile(fileId);

    const link = document.createElement('a');
    link.href = URL.createObjectURL(response);
    link.download = fileName;
    link.click();
  };

  if (!content) {
    return null;
  }

  return (
    <>
      <div className={styles.svgContainer}>
        <div id={file.id.toString()} className="svg-attachment-scroll-margin"></div>
        <div className={styles.svgOverlayActions}>
          <ExpandDialog triggerRef={dialogTriggerRef} title={file.name}>
            {content && <img src={`data:image/svg+xml;utf8,${encodeURIComponent(content)}`} alt={file.name} />}
          </ExpandDialog>
        </div>
        <div ref={containerRef} className={styles.svgContent}>
          <div className={styles.svgInner} dangerouslySetInnerHTML={{ __html: content }} />
        </div>
      </div>
    </>
  );
});

const ExpandDialog = ({ children, triggerRef, title }) => {
  return (
    <Dialog>
      <DialogTrigger ref={triggerRef}></DialogTrigger>
      <DialogContent className={styles.dialogContent}>
        <DialogTitle className={styles.dialogTitle}>{title}</DialogTitle>
        <div className="mt-4">{children}</div>
      </DialogContent>
    </Dialog>
  );
};

export default ExpandDialog;
